import { ShadowSettingKey } from '@lessonup/pins-shared';
import { styled } from '@lessonup/ui-components';
import React, { useEffect, useRef, useState } from 'react';
import { clampPercentage, computeShadowFilter, PERCENTAGE_MULTIPLIER } from '../../utils';
import { calculateCircularProgressFromClientPoint } from '../../utils/pinComponents/audioPinComponent.utils';

// Progress circle constants
const PROGRESS_STROKE_WIDTH = 12;
const PROGRESS_OFFSET = 6; // Distance from edge to progress circle
const PROGRESS_TRANSITION_DURATION = '0.25s'; // chrome seems to throttle the time update to +/- 250ms
const SCRUB_TIMEOUT_DURATION = 200; // 200ms after mouse down, we start scrubbing

const PLAYER_CUTOFF_BUG_MARGIN = 1; // chrome has a bug where the progress circle is cut off on the bottom and right. This is a hack to fix it.

export interface ProgressCircleProps {
  size: { width: number; height: number };
  progress: number;
  disableAllClicks?: boolean;
  borderColor: string;
  borderColorProgress: string;
  shadow?: ShadowSettingKey;
  onChange?: (progress: number) => void;
  onScrubStart?: () => void;
  onScrubEnd?: () => void;
}

/**
 * Progress circle that displays progress and supports clicking and dragging to scrub.
 */
export const ProgressCircle: React.FC<ProgressCircleProps> = ({
  size,
  progress,
  disableAllClicks,
  borderColor,
  borderColorProgress,
  shadow,
  onChange,
  onScrubStart,
  onScrubEnd,
}) => {
  const [isScrubbing, setIsScrubbing] = useState(false);
  const scrubRectRef = useRef<DOMRect | null>(null);
  const lastHandleMouseMove = useRef<((event: MouseEvent) => void) | null>(null);
  const lastHandleMouseUp = useRef<(() => void) | null>(null);
  const scrubTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const radius = Math.min(size.width, size.height) / 2 - PROGRESS_OFFSET;
  const centerX = size.width / 2;
  const centerY = size.height / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDasharray = circumference;
  const clampedProgress = clampPercentage(progress);
  const strokeDashoffset = circumference - (clampedProgress / PERCENTAGE_MULTIPLIER) * circumference;

  const updateProgressFromMousePosition = (clientX: number, clientY: number, rect: DOMRect) => {
    const percentage = calculateCircularProgressFromClientPoint({
      clientX,
      clientY,
      rect,
    });
    onChange?.(percentage);
  };

  const handleMouseDown = (event: React.MouseEvent<SVGSVGElement>) => {
    if (disableAllClicks) return;

    // Capture the SVG element's bounding rect for stable drag calculations
    const svgElement = event.currentTarget;
    const rect = svgElement.getBoundingClientRect();
    scrubRectRef.current = rect;

    // Store event handlers for cleanup
    lastHandleMouseMove.current = handleMouseMove;
    lastHandleMouseUp.current = handleMouseUp;

    // Add global event listeners for drag behavior
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    // we do do not want to trigger onScrubStart on click
    scrubTimeoutRef.current = setTimeout(() => {
      setIsScrubbing(true);
      onScrubStart?.();
      scrubTimeoutRef.current = null;
    }, SCRUB_TIMEOUT_DURATION);
  };

  const handleMouseMove = (event: MouseEvent) => {
    event.stopPropagation();

    const rect = scrubRectRef.current;
    if (!rect) return;

    updateProgressFromMousePosition(event.clientX, event.clientY, rect);
  };

  const handleMouseUp = () => {
    // Clear the timeout if it hasn't fired yet
    if (scrubTimeoutRef.current) {
      clearTimeout(scrubTimeoutRef.current);
      scrubTimeoutRef.current = null;
    } else {
      onScrubEnd?.();
    }

    setIsScrubbing(false);

    // Clear the stored rect to prevent onClick from using the wrong rect
    scrubRectRef.current = null;

    // Remove global event listeners
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
    lastHandleMouseMove.current = null;
    lastHandleMouseUp.current = null;
  };

  const handleClick = (event: React.MouseEvent<SVGSVGElement>) => {
    if (disableAllClicks) return;

    const rect = event.currentTarget.getBoundingClientRect();
    updateProgressFromMousePosition(event.clientX, event.clientY, rect);
  };

  // Cleanup event listeners and timeout on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      if (lastHandleMouseMove.current) {
        document.removeEventListener('mousemove', lastHandleMouseMove.current);
      }
      if (lastHandleMouseUp.current) {
        document.removeEventListener('mouseup', lastHandleMouseUp.current);
      }
      if (scrubTimeoutRef.current) {
        clearTimeout(scrubTimeoutRef.current);
      }
    };
  }, []);

  return (
    <StyledSvg
      width={size.width + PLAYER_CUTOFF_BUG_MARGIN}
      height={size.height + PLAYER_CUTOFF_BUG_MARGIN}
      onClick={handleClick}
      onMouseDown={handleMouseDown}
      isScrubbing={isScrubbing}
      disableAllClicks={disableAllClicks}
      style={{
        filter: shadow ? `drop-shadow(${computeShadowFilter(shadow)})` : 'none',
      }}
    >
      <StyledBackgroundCircle cx={centerX} cy={centerY} r={radius} stroke={borderColor} />
      <StyledProgressCircle
        cx={centerX}
        cy={centerY}
        r={radius}
        stroke={borderColorProgress}
        strokeDasharray={strokeDasharray}
        strokeDashoffset={strokeDashoffset}
        transform={`rotate(-90 ${centerX} ${centerY})`}
        isScrubbing={isScrubbing}
      />
    </StyledSvg>
  );
};

// Styled components
const StyledSvg = styled.svg<{
  isScrubbing: boolean;
  disableAllClicks?: boolean;
}>`
  position: absolute;
  top: 0;
  left: 0;
  pointer-events: ${({ disableAllClicks }) => (disableAllClicks ? 'none' : 'auto')};
  cursor: ${({ isScrubbing }) => (isScrubbing ? 'grabbing' : 'pointer')};
`;

const StyledBackgroundCircle = styled.circle`
  fill: none;
  stroke-width: ${PROGRESS_STROKE_WIDTH};
`;

const StyledProgressCircle = styled.circle<{
  isScrubbing: boolean;
}>`
  fill: none;
  stroke-linecap: round;
  stroke-width: ${PROGRESS_STROKE_WIDTH};
  transition: ${({ isScrubbing }) =>
    isScrubbing ? 'none' : `stroke-dashoffset ${PROGRESS_TRANSITION_DURATION} ease-out`};
`;
